import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {map, Observable, switchMap} from 'rxjs';
import {Company} from '../../model/proposal/company';
import {CompanyPage} from '../../model/company-page';
import {ImageReader} from '../../util/image-reader';
import {UserService} from '../user/user.service';
import {CompanyRole} from '../../model/company-role';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {

  titlePictures = new Map<string, Observable<any>>();
  logos = new Map<string, Observable<any>>();

  constructor(private readonly http: HttpClient,
              private readonly userService: UserService) {
  }

  saveCompany(company: Company) {
    return this.http.post(environment.backend + '/v1/companies', company);
  }

  getCompany(companyName: string): Observable<Company> {
    return this.http.get<Company>(environment.backend + '/v1/companies/' + companyName);
  }

  getAllCompanies(): Observable<CompanyPage[]> {
    return this.http.get<CompanyPage[]>(environment.backend + '/v1/companies');
  }

  getCompanyPage(companyName: string): Observable<CompanyPage> {
    return this.http.get<CompanyPage>(environment.backend + `/v1/companies/${companyName}/page`).pipe(map(companyPage => this.mapCompanyPage(companyPage)));
  }

  mapCompanyPage(companyPage: CompanyPage) {
    const mappedCompanyPage = new CompanyPage(companyPage);
    mappedCompanyPage.titlePicture = ImageReader.convertImage(companyPage.titlePicture);
    mappedCompanyPage.logo = ImageReader.convertImage(companyPage.logo);
    mappedCompanyPage.offeringLOBs = companyPage.offeringLOBs;
    mappedCompanyPage.lookingForLOBs = companyPage.lookingForLOBs;
    mappedCompanyPage.offeringExpertServices = companyPage.offeringExpertServices;
    mappedCompanyPage.users = companyPage.users.map(user => this.userService.mapUser(user));
    return mappedCompanyPage;
  }

  setLogo(companyId: string, image: any): Observable<any> {
    const formData = new FormData();
    formData.append('logo', image);
    return this.http.post(environment.backend + `/v1/companies/${companyId}/pictures/logo`, formData);
  }

  deleteLogo(companyId: string): Observable<any> {
    return this.http.delete(environment.backend + `/v1/companies/${companyId}/pictures/logo`);
  }

  getLogo(companyId: string): Observable<any> {
    if (this.logos.has(companyId)) {
      return this.logos.get(companyId);
    }
    const logo = this.http.get(environment.backend + `/v1/companies/${companyId}/pictures/logo`, {responseType: 'blob'}).pipe(switchMap(value => ImageReader.blobToData(value)));
    this.logos.set(companyId, logo);
    return this.logos.get(companyId);
  }

  getTitlePicture(companyId: string): Observable<any> {
    if (this.titlePictures.has(companyId)) {
      return this.titlePictures.get(companyId);
    }
    const image = this.http.get(environment.backend + `/v1/companies/${companyId}/pictures/title`, {responseType: 'blob'}).pipe(switchMap(value => ImageReader.blobToData(value)));
    this.titlePictures.set(companyId, image);
    return this.titlePictures.get(companyId);
  }

  setTitlePicture(companyId: string, image: any): Observable<any> {
    const formdata = new FormData();
    formdata.append('titlePicture', image);
    return this.http.post(environment.backend + `/v1/companies/${companyId}/pictures/title`, formdata);
  }

  deleteTitlePicture(companyId: string): Observable<any> {
    return this.http.delete(environment.backend + `/v1/companies/${companyId}/pictures/title`);
  }

  updateCompanyPage(companyId: string, companyPage: CompanyPage) {
    return this.http.patch(environment.backend + `/v1/companies/${companyId}`, companyPage);
  }

  getCompanyRole(companyId: string){
    return this.http.get<CompanyRole>(environment.backend + `/v1/companies/${companyId}/companyRole`);
  }
}
